/**
 * Simple encryption utility for localStorage
 * DRY - Single encrypt/decrypt functions for all data
 */

const SECRET_KEY = "imgrows-secret-key";
const TOKEN_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

// Core cipher functions (DRY - used for all data)
function xorCipher(str) {
  let result = "";
  for (let i = 0; i < str.length; i++) {
    result += String.fromCharCode(str.charCodeAt(i) ^ SECRET_KEY.charCodeAt(i % SECRET_KEY.length));
  }
  return btoa(result);
}

function xorDecipher(str) {
  try {
    const decoded = atob(str);
    let result = "";
    for (let i = 0; i < decoded.length; i++) {
      result += String.fromCharCode(decoded.charCodeAt(i) ^ SECRET_KEY.charCodeAt(i % SECRET_KEY.length));
    }
    return result;
  } catch {
    return null;
  }
}

// Generic storage functions (DRY - single set/get for all encrypted data)
function setEncrypted(key, data) {
  try {
    localStorage.setItem(key, xorCipher(JSON.stringify(data)));
    return true;
  } catch {
    return false;
  }
}

function getEncrypted(key) {
  try {
    const encrypted = localStorage.getItem(key);
    if (!encrypted) return null;
    const decrypted = xorDecipher(encrypted);
    return decrypted ? JSON.parse(decrypted) : null;
  } catch {
    return null;
  }
}

// ===== Auth Data - Single encrypted object =====

export function setAuthData(token, userData) {
  setEncrypted("tk", { token, timestamp: Date.now() });
  setEncrypted("ud", userData);
}

export function getAuthData() {
  const tokenData = getEncrypted("tk");
  const userData = getEncrypted("ud");
  return {
    token: tokenData?.token || null,
    timestamp: tokenData?.timestamp || null,
    user: userData
  };
}

export function clearAuthData() {
  localStorage.removeItem("ud");
  localStorage.removeItem("tk");
}

// ===== Token helpers =====

export function getToken() {
  return getEncrypted("tk")?.token || null;
}

export function isTokenExpired() {
  const tokenData = getEncrypted("tk");
  if (!tokenData?.timestamp) return true;
  return Date.now() - tokenData.timestamp >= TOKEN_EXPIRY_MS;
}

// ===== Redirect helpers =====

export function setPostLoginRedirect(path) {
  try { sessionStorage.setItem("redirectAfterLogin", path); } catch {}
}

export function getPostLoginRedirect() {
  try { return sessionStorage.getItem("redirectAfterLogin"); } catch { return null; }
}

export function clearPostLoginRedirect() {
  try { sessionStorage.removeItem("redirectAfterLogin"); } catch {}
}

// ===== Migration cleanup =====

export function cleanupOldAuthData() {
  const oldKeys = [
    "hasLogin", "user_token", "token_timestamp", "user_id", "user_type",
    "userName", "userEmail", "userProfile", "signup_process_no",
    "current_organisation", "current_organisation_user_type", "parent_organisation",
    "onBoarding", "help_flow", "paid", "completed_steps", "keep_me_signed_in",
    "status", "status_message", "user_data"
  ];
  oldKeys.forEach(key => localStorage.removeItem(key));
}

export default {
  setAuthData,
  getAuthData,
  clearAuthData,
  getToken,
  isTokenExpired,
  setPostLoginRedirect,
  getPostLoginRedirect,
  clearPostLoginRedirect,
  cleanupOldAuthData,
};
